
import React, { useState, useRef, useEffect } from 'react';
import { ColumnDefinition, HitPolicy } from '../../types';
import { Settings, ChevronDown } from 'lucide-react';

// --- Header Cell ---
interface HeaderCellProps {
  column: ColumnDefinition;
  isFirst?: boolean;
  isLast?: boolean;
  onUpdate?: (id: string, def: Partial<ColumnDefinition>) => void;
  key?: React.Key;
}

export const HeaderCell = ({ column, isFirst, isLast, onUpdate }: HeaderCellProps) => {
  const isInput = column.type === 'input';
  
  return (
    <th 
      tabIndex={0}
      className={`
        relative p-0 border-r border-b border-slate-300 dark:border-slate-600 min-w-[150px]
        ${isInput ? 'bg-slate-100 dark:bg-slate-800' : 'bg-slate-200 dark:bg-slate-700'}
        ${isLast ? 'border-r-0' : ''}
        focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/40
      `}
    >
      <div className="flex flex-col h-full group">
        <div className="flex-1 p-2 flex items-center justify-center font-bold text-xs text-slate-700 dark:text-slate-200 uppercase tracking-wide">
          <input 
             type="text"
             className="bg-transparent text-center w-full focus:outline-none focus:border-b focus:border-primary"
             value={column.label}
             onChange={(e) => onUpdate?.(column.id, { label: e.target.value })}
          />
        </div>
        <div className="h-6 flex items-center justify-center border-t border-slate-300/50 dark:border-slate-600 text-[10px] text-slate-500 dark:text-slate-400 font-mono bg-white/30 dark:bg-slate-900/30 gap-1 px-2">
             <input 
               className="bg-transparent text-right w-1/2 focus:outline-none focus:text-primary hover:text-slate-700 dark:hover:text-slate-200"
               value={column.variableName || ''}
               placeholder="var_name"
               onChange={(e) => onUpdate?.(column.id, { variableName: e.target.value })}
             />
             <span className="text-slate-300 dark:text-slate-600">|</span>
             <select 
                value={column.dataType}
                onChange={(e) => onUpdate?.(column.id, { dataType: e.target.value as any })}
                className="bg-transparent text-left w-1/2 focus:outline-none hover:text-primary cursor-pointer text-[10px] text-slate-500 dark:text-slate-400 font-mono"
             >
                <option value="string">string</option>
                <option value="number">number</option>
                <option value="boolean">boolean</option>
                <option value="date">date</option>
             </select>
        </div>
      </div>
    </th>
  );
};

// --- Hit Policy Cell (Top Left Corner) ---
export const HitPolicyCell = ({ policy, onClick }: { policy: HitPolicy, onClick: () => void }) => {
  const policyCodes: Record<HitPolicy, string> = {
    UNIQUE: 'U', FIRST: 'F', PRIORITY: 'P', ANY: 'A', COLLECT: 'C', 'RULE ORDER': 'R', 'OUTPUT ORDER': 'O'
  };

  return (
    <th 
      tabIndex={0}
      className="w-12 bg-slate-800 dark:bg-slate-800 text-white border-r border-b border-slate-600 dark:border-slate-600 cursor-pointer hover:bg-slate-700 dark:hover:bg-slate-700 transition-colors z-10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white/40"
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      title={`Hit Policy: ${policy}`}
    >
      <div className="flex items-center justify-center h-full font-bold text-lg font-mono">
        {policyCodes[policy] || '?'}
      </div>
    </th>
  );
};


// --- Editable Body Cell ---
interface EditableCellProps {
  value: string;
  onChange: (value: string) => void;
  isOutput?: boolean;
  autoFocus?: boolean;
  isInvalid?: boolean;
  key?: React.Key;
}

export const EditableCell = ({ value, onChange, isOutput, autoFocus, isInvalid }: EditableCellProps) => {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const tdRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    if (autoFocus) {
      setEditing(true);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // Select text on focus for easier editing
      inputRef.current.select();
    }
  }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    onChange(tempValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      inputRef.current?.blur();
      // After blurring input, return focus to the TD so tab flow continues
      tdRef.current?.focus();
    }
    if (e.key === 'Escape') {
      setTempValue(value);
      setEditing(false);
      tdRef.current?.focus();
    }
  };

  const handleTdKeyDown = (e: React.KeyboardEvent) => {
    if (!editing && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      setEditing(true);
    }
  };

  const handleFocus = (e: React.FocusEvent) => {
    // If the TD itself gets focus (e.g. via Tab), enter editing mode
    if (e.target === tdRef.current && !editing) {
      setEditing(true);
    }
  };

  return (
    <td 
      ref={tdRef}
      tabIndex={editing ? -1 : 0} // When editing, the input handles focus
      onClick={() => setEditing(true)}
      onFocus={handleFocus}
      onKeyDown={handleTdKeyDown}
      className={`
        border-r border-b border-slate-200 dark:border-slate-700 h-10 px-3 py-2 text-sm font-mono cursor-text min-w-[150px] transition-colors relative
        focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary/50
        ${isOutput ? 'text-slate-600 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/30 hover:bg-slate-100 dark:hover:bg-slate-800/60' : 'text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/40'}
        ${!value || value === '-' ? 'text-slate-400 dark:text-slate-600 italic' : ''}
        ${editing ? 'p-0 ring-2 ring-inset ring-primary' : ''}
        ${isInvalid ? 'bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 border-red-200 dark:border-red-900 ring-1 ring-inset ring-red-500' : ''}
      `}
    >
      {editing ? (
        <input
          ref={inputRef}
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={`absolute inset-0 w-full h-full px-3 py-2 text-sm font-mono focus:outline-none ${isInvalid ? 'bg-red-50 dark:bg-red-900 text-red-700 dark:text-red-200' : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100'}`}
        />
      ) : (
        <div className="truncate w-full">
          {value || '-'}
        </div>
      )}
    </td>
  );
};
